import { useState, type ReactNode } from 'react';
import { type ExamSessionView, type RosterCandidateOption } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field, FieldRow } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { TimePicker } from '../../../components/TimePicker';
import { dhakaToIso, isoToDhaka } from '../api/exam-sessions-api';
import styles from './Assessments.module.css';

// S18 — the small anchored panels ASM-04 opens beside the control that asked for them
// (CLAUDE.md §5 "pop-ups anchor to the control that opened them").

/** F9.14 — move: a new date, time and venue. Everyone booked is told. */
export function MovePanel({ session, onMove, onCancel }: { readonly session: ExamSessionView; readonly onMove: (startsAt: string, venue: string) => void; readonly onCancel: () => void }): ReactNode {
  const now = isoToDhaka(session.startsAt);
  const [date, setDate] = useState(now.date);
  const [time, setTime] = useState(now.time);
  const [venue, setVenue] = useState(session.venue ?? '');
  const startsAt = dhakaToIso(date, time);
  const isChanged = startsAt !== session.startsAt || venue.trim() !== (session.venue ?? '');
  return (
    <div className={styles.confirm}>
      <p className={styles.confirmTitle}>Move {session.name}</p>
      <FieldRow>
        <Field label="Date" hint="Dhaka time.">
          {({ inputId }) => <DatePicker id={inputId} label="Date" value={date} onChange={setDate} />}
        </Field>
        <Field label="Start time">
          {({ inputId }) => <TimePicker id={inputId} label="Start time" value={time} onChange={setTime} />}
        </Field>
      </FieldRow>
      <Field label="Venue">{({ inputId }) => <Input id={inputId} value={venue} onChange={(event) => setVenue(event.target.value)} />}</Field>
      <p className={styles.confirmBody}>
        {session.booked > 0 ? `All ${session.booked} people booked are emailed the new details.` : 'Nobody is booked yet.'} The list reopens and locks again a day before the new time.
      </p>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Keep it
        </Button>
        <Button variant="solid" size="sm" disabled={!startsAt || venue.trim() === '' || !isChanged} onClick={() => startsAt && onMove(startsAt, venue.trim())}>
          Move and tell everyone
        </Button>
      </div>
    </div>
  );
}

/** F9.14 — cancel, with the reason §L25 requires. Irreversible, so it confirms (§5a rule 8). */
export function CancelPanel({ session, onConfirm, onCancel }: { readonly session: ExamSessionView; readonly onConfirm: (reason: string) => void; readonly onCancel: () => void }): ReactNode {
  const [reason, setReason] = useState('');
  return (
    <div className={styles.confirm}>
      <p className={styles.confirmTitle}>Cancel {session.name}?</p>
      <Field label="Why" isRequired hint="Kept on the record. Candidates are told the exam is cancelled.">
        {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} rows={2} value={reason} placeholder="The venue is unavailable that day." onChange={(event) => setReason(event.target.value)} />}
      </Field>
      <p className={styles.confirmBody}>
        {session.booked > 0 ? `All ${session.booked} people booked are emailed, and each can be booked on another session.` : 'Nobody is booked yet.'}
      </p>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Keep it
        </Button>
        <Button variant="danger" size="sm" disabled={reason.trim() === ''} onClick={() => onConfirm(reason.trim())}>
          Cancel the session
        </Button>
      </div>
    </div>
  );
}

/** F9.09 — who can go on the list: people in Assessment on the job. */
export function AddPeoplePanel({
  options,
  isLoading,
  seatsLeft,
  onAdd,
  onCancel,
}: {
  readonly options: readonly RosterCandidateOption[];
  readonly isLoading: boolean;
  readonly seatsLeft: number;
  readonly onAdd: (applicationIds: readonly string[]) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const free = options.filter((option) => option.blockedBy === null);

  function toggle(id: string, isOn: boolean): void {
    setChosen((current) => {
      const next = new Set(current);
      if (isOn) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  return (
    <div className={styles.confirm}>
      <p className={styles.confirmTitle}>Add people · {seatsLeft} seat{seatsLeft === 1 ? '' : 's'} left</p>
      {isLoading ? (
        <SkeletonLines count={3} />
      ) : options.length === 0 ? (
        <p className={styles.confirmBody}>Nobody else is in the Assessment stage. In the job's Contact step, select the people who confirmed and use "Add to exam session".</p>
      ) : (
        <div className={styles.pool}>
          {free.length > 1 && (
            <Checkbox label={`Everyone who can be booked (${free.length})`} checked={free.every((option) => chosen.has(option.applicationId))} onChange={(isOn) => setChosen(isOn ? new Set(free.map((option) => option.applicationId)) : new Set())} />
          )}
          {options.map((option) => (
            <div key={option.applicationId} className={styles.poolItem}>
              <Checkbox label={option.candidateName} checked={chosen.has(option.applicationId)} disabled={option.blockedBy !== null} onChange={(isOn) => toggle(option.applicationId, isOn)} />
              {option.blockedBy && <span className={styles.meta}>{option.blockedBy}</span>}
            </div>
          ))}
        </div>
      )}
      {chosen.size > seatsLeft && <p className={styles.warning}>Only {seatsLeft} fit. The rest will be refused.</p>}
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" disabled={chosen.size === 0} onClick={() => onAdd([...chosen])}>
          Add {chosen.size > 0 ? chosen.size : ''} and email them
        </Button>
      </div>
    </div>
  );
}

/** Taking someone off sends them an email, so it is not an undoable click (§L9). */
export function RemovePanel({ name, onConfirm, onCancel }: { readonly name: string; readonly onConfirm: () => void; readonly onCancel: () => void }): ReactNode {
  return (
    <div className={styles.confirm}>
      <p className={styles.confirmTitle}>Take {name} off the list?</p>
      <p className={styles.confirmBody}>They are emailed that the exam is cancelled for them, and the seat frees up.</p>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Keep them
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm}>
          Take off
        </Button>
      </div>
    </div>
  );
}
